var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { NavLink } from 'react-router-dom';
import { Text, Box, NoData, NoShowIndicator } from 'shared/components';
import { RiskTableCell, StatusTable, TableCellLink } from 'shared/features/table';
import { formatFullDate, formatFullDateTime, getAgeFromFormattedDob, linkToNoteOrPlaceholder, } from 'shared/utils';
import { appointmentKindLabels } from 'constants/appointmentKind';
import { Focus } from 'shared/assets/images/Focus';
import { appointmentStatus } from 'constants/ehrNoteEnums';
import { Info } from 'shared/assets/icons';
import { defaultPageSizeOptions } from 'constants/table';
import { useFlaggedToDoAppointmentsAndNotes } from 'src/shared/hooks/useFlaggedAppointmentsAndNotes';
/**
 * The ToDo component displays a table of todo notes for the provider.
 * It lists notes that are: Overdue, Draft and Not Started
 * The user the can click on:
 *    A client's name to view details about the client
 *    A note in the Note Status column to view the note
 *    An appointment in the Appointment column to view the appointment
 *
 * @returns {React.ReactElement}
 */
var ToDo = function () {
    var _a = useFlaggedToDoAppointmentsAndNotes(), notesAndPlaceholders = _a.notesAndPlaceholders, isError = _a.isError, isLoading = _a.isLoading;
    var SortableNoteStatuses;
    (function (SortableNoteStatuses) {
        SortableNoteStatuses[SortableNoteStatuses["overdue"] = 0] = "overdue";
        SortableNoteStatuses[SortableNoteStatuses["draft"] = 1] = "draft";
        SortableNoteStatuses[SortableNoteStatuses["notStarted"] = 2] = "notStarted";
    })(SortableNoteStatuses || (SortableNoteStatuses = {}));
    var noteStatusLabels = ['Overdue', 'Draft', 'Not Started'];
    /**
     * Calculate the status of the note based on either the start_at or updated_at date
     *
     * @param {EHRNote} note - The note to calculate the status for
     * @param {OriginalRow} originalRow - The original row data from the table
     * @returns {number} - The status enum of the note
     */
    var calculateNoteStatus = function (originalRow) {
        var _a;
        return originalRow.overdue
            ? SortableNoteStatuses.overdue
            : ((_a = originalRow.note) === null || _a === void 0 ? void 0 : _a.updatedAt)
                ? SortableNoteStatuses.draft
                : SortableNoteStatuses.notStarted;
    };
    var cols = [
        {
            Header: 'Name',
            accessor: function (row) { var _a; return (_a = row === null || row === void 0 ? void 0 : row.member) === null || _a === void 0 ? void 0 : _a.last_name; },
            Cell: function (_a) {
                var row = _a.row;
                var member = ((row === null || row === void 0 ? void 0 : row.original) || {}).member;
                var age = getAgeFromFormattedDob(member === null || member === void 0 ? void 0 : member.date_of_birth);
                return (_jsx(Box, __assign({ m: 2, maxWidth: "260px" }, { children: _jsxs(TableCellLink, __assign({ to: "/client/".concat(member === null || member === void 0 ? void 0 : member.id, "/demographics"), "data-testid": member === null || member === void 0 ? void 0 : member.first_name, subtitle: "".concat(member === null || member === void 0 ? void 0 : member.date_of_birth, " (").concat(age, " yo)") }, { children: [member === null || member === void 0 ? void 0 : member.first_name, " ", member === null || member === void 0 ? void 0 : member.last_name, ' '] })) })));
            },
        },
        {
            Header: 'Note status',
            id: 'noteStatus',
            isSorted: true,
            accessor: function (row) { return calculateNoteStatus(row); },
            Cell: function (_a) {
                var _b, _c;
                var row = _a.row;
                var noteStatus = noteStatusLabels[calculateNoteStatus(row.original)];
                var lastTouched = ((_c = (_b = row.original) === null || _b === void 0 ? void 0 : _b.note) === null || _c === void 0 ? void 0 : _c.updatedAt) || '';
                return (_jsx(Box, __assign({ m: 2 }, { children: _jsx(TableCellLink, __assign({ "data-testid": "noteStatus", to: linkToNoteOrPlaceholder(row.original), subtitle: formatFullDateTime(lastTouched) }, { children: noteStatus })) })));
            },
        },
        {
            Header: 'Date/Time',
            accessor: 'date',
            Cell: function (_a) {
                var _b, _c, _d, _e, _f;
                var row = _a.row;
                var status = (_b = row.original.note) === null || _b === void 0 ? void 0 : _b.sessionInfo.status;
                return (_jsx(Box, __assign({ m: 2 }, { children: _jsxs(TableCellLink, __assign({ "data-testid": "appointmentDate", to: linkToNoteOrPlaceholder(row.original), subtitle: ((_c = row.original) === null || _c === void 0 ? void 0 : _c.appointment)
                            ? appointmentKindLabels[(_e = (_d = row.original) === null || _d === void 0 ? void 0 : _d.appointment) === null || _e === void 0 ? void 0 : _e.kind]
                            : 'Unscheduled Interaction' }, { children: [status && status !== appointmentStatus.completed && (_jsx(NoShowIndicator, { showStatus: status })), formatFullDateTime((_f = row === null || row === void 0 ? void 0 : row.original) === null || _f === void 0 ? void 0 : _f.date, true)] })) })));
            },
        },
        {
            Header: 'Provider reported',
            headerIcon: Info,
            headerTooltipTitle: 'Risk',
            headerTooltipDescription: 'Risk level is reported by you and is based on the highest risk level from your last appointment, whether it’s self-harm or harm to others.',
            accessor: '',
            id: 'risk',
            // this time assume it's an ehr note instead of base note for risk stuff, null check for when it's not.
            Cell: function (_a) {
                var row = _a.row;
                var riskConcerns = row.original.riskConcerns;
                var riskForHarmToSelf = riskConcerns.riskForHarmToSelf, riskForHarmToOthers = riskConcerns.riskForHarmToOthers;
                return (_jsx(RiskTableCell, __assign({ riskToSelf: riskForHarmToSelf || undefined, riskToOthers: riskForHarmToOthers || undefined }, { children: _jsx(NavLink, __assign({ to: linkToNoteOrPlaceholder(row.original) }, { children: _jsx(Text, __assign({ mt: 2, size: 'small' }, { children: formatFullDate(riskConcerns.date || '') })) })) })));
            },
        },
    ];
    /**
     * Render the component
     */
    return (_jsx(_Fragment, { children: _jsx(StatusTable, { cols: cols, data: notesAndPlaceholders, initialState: { sortBy: [{ id: 'noteStatus', desc: false }] }, isLoading: isLoading, isError: isError, noData: _jsx(NoData, { title: "Sweet! You have no outstanding notes.", icon: _jsx(Focus, {}) }), maxWidth: '100%', paginated: true, pageSizeOptions: defaultPageSizeOptions, preservedTableId: "todo" }) }));
};
export { ToDo };
